import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Avatar } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftRight, LogOut, User } from 'lucide-react';
import styles from './UserMenu.module.scss';

// Distance from the trigger's own edges, in px — matches the old
// position: absolute rule's `top: calc(100% + var(--spacing-4))` and
// `right: calc(-1 * var(--spacing-2))` (spacing-4 = 16px, spacing-2 = 8px).
const MENU_OFFSET_TOP = 16;
const MENU_OFFSET_RIGHT = 8;

const MENU_ANIMATION = {
  initial: { opacity: 0, y: -6, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -6, scale: 0.96 },
  transition: { type: 'spring', stiffness: 400, damping: 28 },
} as const;

export interface UserMenuProps {
  displayName: string;
  email?: string;
  avatarUrl?: string;
  /** Whether the surface-switch item shows at all — only a SUPER_ADMIN gets one. */
  showSurfaceSwitch: boolean;
  /** Whether the candidate/admin surface currently showing is the admin one — flips the switch item's label and the avatar's accent. */
  isAdminSurface: boolean;
  onSwitchSurface: () => void;
  onSignOut: () => void;
}

/**
 * Avatar trigger + dropdown: a profile card (name/email, left-aligned like
 * the items below it) on top, then the Admin/Practice switch (SUPER_ADMIN
 * only) and Sign out below a divider.
 * Adapted from permits-ai's UserMenu, restyled with this app's solid
 * floating surface (see the floating-surface mixin).
 *
 * The dropdown itself is portaled to document.body rather than left as a
 * child of the trigger — the trigger lives inside MainLayout's .headerBar,
 * and portaling escapes the header pill's stacking context and clipping.
 */
export const UserMenu: React.FC<UserMenuProps> = ({
  displayName,
  email,
  avatarUrl,
  showSurfaceSwitch,
  isAdminSurface,
  onSwitchSurface,
  onSignOut,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  // Computed from the trigger's own on-screen rect, not CSS relative
  // positioning — the panel is portaled out of the trigger's DOM subtree
  // (see the component doc comment), so it has no positioning ancestor to
  // anchor to otherwise. The header is `position: sticky; top: 0`, so the
  // trigger's viewport rect is stable once scrolled — no scroll listener
  // needed, only resize (e.g. sidebar collapse/expand or viewport resize).
  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({
        top: rect.bottom + MENU_OFFSET_TOP,
        right: window.innerWidth - rect.right - MENU_OFFSET_RIGHT,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (menuContainerRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-label={`Account menu for ${displayName}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar
          size={36}
          src={avatarUrl}
          icon={!avatarUrl && <User style={{ width: '1em', height: '1em' }} />}
          className={isAdminSurface ? `${styles.avatar} ${styles.avatarAdmin}` : styles.avatar}
        />
      </button>

      {createPortal(
        <AnimatePresence>
          {open && position && (
            <motion.div
              className={styles.menu}
              ref={menuContainerRef}
              style={{ top: position.top, right: position.right }}
              {...MENU_ANIMATION}
            >
              <div className={styles.card}>
                <span className={styles.cardName}>{displayName}</span>
                {email && <span className={styles.cardEmail}>{email}</span>}
              </div>

              <div className={styles.divider} />

              <div className={styles.items}>
                {showSurfaceSwitch && (
                  <button
                    type="button"
                    className={styles.item}
                    onClick={() => {
                      setOpen(false);
                      onSwitchSurface();
                    }}
                  >
                    <ArrowLeftRight size={18} className={styles.itemIcon} />
                    {isAdminSurface ? 'Practice' : 'Admin'}
                  </button>
                )}
                <button
                  type="button"
                  className={styles.item}
                  onClick={() => {
                    setOpen(false);
                    onSignOut();
                  }}
                >
                  <LogOut size={18} className={styles.itemIcon} />
                  Sign out
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default UserMenu;
